import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, Circle, MapPinned } from "lucide-react";
import { api } from "@/lib/api";
import { addDays, fmtTime, parseLocal, startOfDay, startOfWeek, todayStr, ymd } from "@/lib/dates";
import { Calendar } from "@/components/ui/calendar";
import RescheduleDialog, { VisitHistory } from "@/components/RescheduleDialog";
import { ClientPhone, RescheduleTaskButton } from "@/components/TaskExtras";

/** Dashboard widget: month picker with task dots + the selected day's tasks (today by default). */
export default function TaskCalendarWidget() {
  const [rescheduling, setRescheduling] = useState(null);
  const [day, setDay] = useState(() => startOfDay(new Date()));
  const [month, setMonth] = useState(() => new Date());
  const gridStart = startOfWeek(new Date(month.getFullYear(), month.getMonth(), 1));

  const q = useQuery({
    queryKey: ["tasks", "range", ymd(gridStart), ymd(addDays(gridStart, 42)), "all"],
    queryFn: async () => (await api.get("/tasks", { params: { start: ymd(gridStart), end: ymd(addDays(gridStart, 42)) } })).data.items,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const all = q.data || [];
  const key = ymd(day);
  const dayTasks = all.filter((t) => t.due_at.slice(0, 10) === key).sort((a, b) => a.due_at.localeCompare(b.due_at));
  const busy = [...new Set(all.filter((t) => !t.done).map((t) => t.due_at.slice(0, 10)))].map((s) => parseLocal(s));
  const isToday = key === todayStr();

  return (
    <section className="soft-card p-4 sm:p-6" data-testid="task-calendar-widget">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-lg font-bold text-navy"><CalendarDays className="h-5 w-5" /> Task Calendar</h3>
        <Link to="/tasks" className="text-xs font-semibold text-navy hover:underline" data-testid="task-widget-open-tasks">Open Tasks →</Link>
      </div>
      <div className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)]">
        <Calendar
          mode="single"
          selected={day}
          onSelect={(d) => d && setDay(startOfDay(d))}
          month={month}
          onMonthChange={setMonth}
          weekStartsOn={1}
          modifiers={{ busy }}
          modifiersClassNames={{ busy: "task-busy-day" }}
          className="mx-auto rounded-xl border border-slate-100"
          data-testid="task-widget-calendar"
        />
        <div className="min-w-0">
          <div className="mb-2 flex items-baseline justify-between">
            <div className="font-semibold text-navy" data-testid="task-widget-day-label">
              {isToday ? "Today" : day.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}
            </div>
            <div className="text-xs text-slate-500" data-testid="task-widget-count">
              {dayTasks.filter((t) => !t.done).length} pending · {dayTasks.filter((t) => t.done).length} done
            </div>
          </div>
          {dayTasks.length === 0 ? (
            <div className="rounded-xl bg-slate-50 py-10 text-center text-sm text-slate-400" data-testid="task-widget-empty">No tasks on this day.</div>
          ) : (
            <ul className="max-h-80 space-y-1.5 overflow-y-auto" data-testid="task-widget-list">
              {dayTasks.map((t) => <TaskRow key={t.id} t={t} onReschedule={setRescheduling} />)}
            </ul>
          )}
        </div>
      </div>
      <RescheduleDialog task={rescheduling} onOpenChange={(v) => !v && setRescheduling(null)} />
    </section>
  );
}

function TaskRow({ t, onReschedule }) {
  const overdue = !t.done && t.due_at.slice(0, 10) < todayStr();
  return (
    <li className="flex items-start gap-3 rounded-xl border border-slate-100 px-3 py-2" data-testid={`task-widget-item-${t.id}`}>
      <div className="w-16 shrink-0 pt-0.5 text-xs font-bold tabular-nums text-navy">{fmtTime(t.due_at)}</div>
      <div className="min-w-0 flex-1">
        <div className={`flex items-start gap-1 text-sm font-medium ${t.done ? "text-slate-400 line-through" : "text-slate-800"}`}>
          {t.kind === "visit" && <MapPinned className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand-gold))]" />}
          <span className="line-clamp-2">{t.title}</span>
        </div>
        <div className="truncate text-[11px] text-slate-500" data-testid={`task-widget-owner-${t.id}`}>{t.owner_name || "—"}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <ClientPhone task={t} />
          <RescheduleTaskButton task={t} onClick={onReschedule} testid={`task-widget-reschedule-${t.id}`} />
        </div>
        <VisitHistory history={t.due_history} testid={`task-widget-history-${t.id}`} label="reschedule history" />
      </div>
      <span
        data-testid={`task-widget-status-${t.id}`}
        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
          t.done ? "bg-emerald-50 text-emerald-700" : overdue ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"
        }`}
      >
        {t.done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
        {t.done ? "Done" : overdue ? "Overdue" : "Pending"}
      </span>
    </li>
  );
}
